import React, { useState } from "react"
import { Button, Input } from "reactstrap"
import configure from "configure";

const StepFeatures = ({ features, setFeatures, services, onPrev, onNext, readOnly, stepLogs, allUsers }) => {
  const [openServices, setOpenServices] = useState({})
  const [bulkMode, setBulkMode]         = useState({})
  const [bulkText, setBulkText]         = useState({})
  const [copied, setCopied]             = useState(false)

  const promptText = `Generate features with sub-features.\n\nFormat:\nFeature Title\n  - Sub-feature 1\n  - Sub-feature 2\nAnother Feature\n  - Sub-feature A\n\nExample:\nUser Login\n  - Login form UI\n  - Form validation\n  - Remember me\nDashboard\n  - Stats overview\n  - Recent activity`

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(promptText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (_) {}
  }

  const toggleService = id =>
    setOpenServices(prev => ({ ...prev, [id]: prev[id] === false ? true : false }))

  // ── Feature CRUD ──────────────────────────────────────────────

  const addFeature = serviceId =>
    setFeatures(prev => {
      const idx = prev.findIndex(s => s.serviceId === serviceId)
      const newF = { title: "", features: [] }
      if (idx === -1) return [...prev, { serviceId, subtitles: [newF] }]
      return prev.map(s =>
        s.serviceId === serviceId ? { ...s, subtitles: [...s.subtitles, newF] } : s
      )
    })

    const isApprovalStillValid = (logs) => {
  if (!logs?.approved?.length) return false;
  if (!logs?.submitted?.length) return true; // approved, never re-submitted
  const lastApprovedAt = new Date(logs.approved.at(-1).at);
  const lastSubmittedAt = new Date(logs.submitted.at(-1).at);
  return lastApprovedAt > lastSubmittedAt; // approved AFTER last submission
};

  const updateFeatureTitle = (serviceId, fi, value) =>
    setFeatures(prev =>
      prev.map(s =>
        s.serviceId === serviceId
          ? { ...s, subtitles: s.subtitles.map((sub, i) => (i === fi ? { ...sub, title: value } : sub)) }
          : s
      )
    )

  const removeFeature = (serviceId, fi) =>
    setFeatures(prev =>
      prev.map(s =>
        s.serviceId === serviceId
          ? { ...s, subtitles: s.subtitles.filter((_, i) => i !== fi) }
          : s
      )
    )

  // ── Sub-feature CRUD ──────────────────────────────────────────

  const addSubFeature = (serviceId, fi) =>
    setFeatures(prev =>
      prev.map(s =>
        s.serviceId === serviceId
          ? {
              ...s,
              subtitles: s.subtitles.map((sub, i) =>
                i === fi ? { ...sub, features: [...sub.features, { title: "" }] } : sub
              ),
            }
          : s
      )
    )

  const updateSubFeature = (serviceId, fi, si, value) =>
    setFeatures(prev =>
      prev.map(s =>
        s.serviceId === serviceId
          ? {
              ...s,
              subtitles: s.subtitles.map((sub, i) =>
                i === fi
                  ? { ...sub, features: sub.features.map((f, j) => (j === si ? { ...f, title: value } : f)) }
                  : sub
              ),
            }
          : s
      )
    )

  const removeSubFeature = (serviceId, fi, si) =>
    setFeatures(prev =>
      prev.map(s =>
        s.serviceId === serviceId
          ? {
              ...s,
              subtitles: s.subtitles.map((sub, i) =>
                i === fi ? { ...sub, features: sub.features.filter((_, j) => j !== si) } : sub
              ),
            }
          : s
      )
    )

  // ── Bulk parse ────────────────────────────────────────────────
  const parseBulkText = text => {
  const lines  = text.split("\n")
  const result = []
  let current  = null

  for (const raw of lines) {
    const line = raw.trimEnd()
    if (!line.trim()) continue

    // Support "-" and "*"
    const isSub = /^[\s]*[-*]\s/.test(line)

    if (isSub) {
      const title = line.replace(/^[\s]*[-*]\s*/, "").trim()
      if (current) current.features.push({ title })
    } else {
      current = { title: line.trim(), features: [] }
      result.push(current)
    }
  }

  return result
}

  const handleBulk = serviceId => {
    const text = bulkText[serviceId] || ""
    if (!text.trim()) return
    const parsed = parseBulkText(text)
    if (!parsed.length) return
    setFeatures(prev => {
      const idx = prev.findIndex(s => s.serviceId === serviceId)
      if (idx === -1) return [...prev, { serviceId, subtitles: parsed }]
      return prev.map(s =>
        s.serviceId === serviceId ? { ...s, subtitles: [...s.subtitles, ...parsed] } : s
      )
    })
    setBulkText(prev => ({ ...prev, [serviceId]: "" }))
    setBulkMode(prev => ({ ...prev, [serviceId]: false }))
  }

  const openBulk  = serviceId => setBulkMode(prev => ({ ...prev, [serviceId]: true }))
  const closeBulk = serviceId => {
    setBulkMode(prev => ({ ...prev, [serviceId]: false }))
    setBulkText(prev => ({ ...prev, [serviceId]: "" }))
  }

  const getGroup = id => features.find(s => s.serviceId === id) || { serviceId: id, subtitles: [] }

  const totalSubs = features.reduce(
    (a, s) => a + s.subtitles.reduce((b, sub) => b + sub.features.length, 0), 0
  )

  // ── Nav buttons (shared, rendered top & bottom) ───────────────
  const NavButtons = () => (
    <div className="rfp-actions">
      {onPrev && (
        <Button outline color="secondary" type="button" onClick={onPrev}>
          ← Previous
        </Button>
      )}
      {onNext && (
        <Button className="rfp-next-btn" type="button" onClick={onNext}>
          Continue →
        </Button>
      )}
    </div>
  )

  return (
    <div className="rfp-features">

      {/* ── Top nav ── */}
      <NavButtons />

      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <h4 className="rfp-title mb-0">
          Features
          {totalSubs > 0 && (
            <span className="rfp-page-sub-count ms-2">({totalSubs} sub-features total)</span>
          )}
        </h4>
      </div>

      {services.map(service => {
        const group         = getGroup(service.value)
        const displayName   = service.title?.trim() || service.label
        const isOpen        = openServices[service.value] !== false
        const isBulkOpen    = !!bulkMode[service.value]
        const currentText   = bulkText[service.value] || ""
        const parsed        = parseBulkText(currentText)
        const detectedF     = parsed.length
        const detectedS     = parsed.reduce((a, f) => a + f.features.length, 0)
        const totalSubsHere = group.subtitles.reduce((a, sub) => a + sub.features.length, 0)

        // scroll when > 3 rows of features (3 cols × 3 rows = 9 cards)
        const featureScrollClass = group.subtitles.length > 9 ? "rfp-feature-grid--scroll" : ""

        return (
          <div key={service.value} className="rfp-inner-card mb-3">

            {/* ── Service header ── */}
            <div
              className={`rfp-service-header ${isOpen ? "rfp-service-header--open" : ""}`}
              onClick={() => toggleService(service.value)}
            >
              <div className="d-flex align-items-center gap-3">
                <h5 className="rfp-service-display-name mb-0">{displayName}</h5>
                <div className="d-flex gap-2">
                  <span className="rfp-count-pill">
                    {group.subtitles.length} feature{group.subtitles.length !== 1 ? "s" : ""}
                  </span>
                  {totalSubsHere > 0 && (
                    <span className="rfp-count-pill">{totalSubsHere} sub</span>
                  )}
                </div>
              </div>

              <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                <Button
                  size="sm"
                  outline
                  color="secondary"
                  type="button"
                  onClick={() => isBulkOpen ? closeBulk(service.value) : openBulk(service.value)}
                >
                  {isBulkOpen ? "Close Bulk" : "Bulk Add"}
                </Button>
                {!readOnly && (
                <Button
                  size="sm"
                  className="rfp-add-btn"
                  type="button"
                  onClick={() => addFeature(service.value)}
                >
                  + Feature
                </Button>
                )}
                <span className="rfp-chevron">{isOpen ? "▾" : "▸"}</span>
              </div>
            </div>

            {/* ── Bulk panel ── */}
            {isBulkOpen && (
              <div className="rfp-bulk-panel">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <span className="rfp-label d-block">Bulk add for "{displayName}"</span>
                    <span className="rfp-bulk-hint">
                      Each line = feature. Prefix sub-features with a dash&nbsp;
                      <code className="rfp-bulk-code">- Sub item</code>
                    </span>
                  </div>
                  <button type="button" className="btn btn-sm btn-success rounded-pill" onClick={copyPrompt}>
                    {copied ? "✓ Copied!" : "Copy AI Prompt"}
                  </button>
                </div>
                <Input
                  type="textarea"
                  rows="6"
                  placeholder={"User Login\n  - Login form UI\n  - Form validation\nDashboard\n  - Stats overview\n  - Recent activity"}
                  value={currentText}
                  onChange={e => setBulkText(prev => ({ ...prev, [service.value]: e.target.value }))}
                />
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <span className="rfp-bulk-detected">
                    {detectedF > 0
                      ? `${detectedF} feature${detectedF !== 1 ? "s" : ""}${detectedS > 0 ? ` · ${detectedS} sub-features` : ""} detected`
                      : "Start typing to detect features"}
                  </span>
                  <div className="d-flex gap-2">
                    <Button size="sm" outline color="secondary" type="button" onClick={() => closeBulk(service.value)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="rfp-generate-btn"
                      type="button"
                      disabled={detectedF === 0}
                      onClick={() => handleBulk(service.value)}
                    >
                      Add {detectedF || ""} Features
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Feature grid ── */}
            {isOpen && (
              <div className="rfp-feature-grid-wrapper">
                {group.subtitles.length === 0 ? (
                  <div className="rfp-empty-sm py-4">
                    No features yet — click <strong>+ Feature</strong> or use <strong>Bulk Add</strong>.
                  </div>
                ) : (
                  <div className={`rfp-feature-grid ${featureScrollClass}`}>
                    {group.subtitles.map((feature, fi) => {
                      // scroll when > 7 sub-features inside a card
                      const subScrollClass = feature.features.length > 7 ? "rfp-subs-list--scroll" : ""

                      return (
                        <div key={fi} className="rfp-feature-card-mini">

                          {/* Card header: sr · title · ✕ */}
                          <div className="rfp-fcm-header">
                            <span className="rfp-fcm-sr">{fi + 1}</span>
                            <Input
                              bsSize="sm"
                              value={feature.title}
                              disabled={readOnly}
                              placeholder="Feature title..."
                              // onChange={e => updateFeatureTitle(service.value, fi, e.target.value)}
                              onChange={e => !readOnly && updateFeatureTitle(service.value, fi, e.target.value)}
                              className="rfp-fcm-input"
                            />
                            {!readOnly && (
                            <button
                              type="button"
                              className="btn rfp-fcm-remove"
                              onClick={() => removeFeature(service.value, fi)}
                              title="Remove feature"
                            >
                              ✕
                            </button>
                            )}
                          </div>

                          {/* Sub-features */}
                          {feature.features.length > 0 && (
                            <div className={`rfp-subs-list ${subScrollClass}`}>
                              {feature.features.map((sf, si) => (
                                <div key={si} className="rfp-sub-row">
                                  <span className="rfp-sub-dot">•</span>
                                  <Input
                                    bsSize="sm"
                                    value={sf.title}
                                    disabled={readOnly}
                                    placeholder="Sub-feature..."
                                    // onChange={e => updateSubFeature(service.value, fi, si, e.target.value)}
                                    onChange={e => !readOnly && updateSubFeature(service.value, fi, si, e.target.value)}
                                    className="rfp-sub-input"
                                  />
                                  {!readOnly && (
                                  <button
                                    type="button"
                                    className="btn rfp-sub-remove"
                                    onClick={() => removeSubFeature(service.value, fi, si)}
                                    title="Remove"
                                  >
                                    ✕
                                  </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Card footer */}
                          <div className="rfp-fcm-footer">
                            {!readOnly && (
                            <button
                              type="button"
                              className="btn rfp-fcm-add-sub-btn"
                              onClick={() => addSubFeature(service.value, fi)}
                            >
                              + Sub
                            </button>
                            )}
                            {feature.features.length > 0 && (
                              <span className="rfp-sub-count ms-auto">
                                {feature.features.length} sub
                              </span>
                            )}
                          </div>

                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        )
      })}

{/* {stepLogs && (
  <div className="rfp-activity-log mt-4">
    <div className="rfp-activity-log-title">Activity</div>
    <div className="d-flex gap-3 flex-wrap">

      {(() => {
        const entry = stepLogs.step2?.saved?.[0];
        if (!entry) return null;
        const user = allUsers?.find((u) => u.id == entry.by);
        return (
          <div className="rfp-activity-pill">
            <div className="rfp-activity-icon">
              {user?.avatar
                ? <img src={`${configure.apiUrl}${user.avatar}`} alt={user.name}
                    style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                : <i className="fas fa-user" />}
            </div>
            <div>
              <div className="rfp-activity-label">Created By</div>
              <div className="rfp-activity-user">{user?.name || `User #${entry.by}`}</div>
              <div className="rfp-activity-time">{new Date(entry.at).toLocaleString()}</div>
            </div>
          </div>
        );
      })()}

      {(() => {
        const entry = stepLogs.step3?.submitted?.at(-1);
        if (!entry) return null;
        const user = allUsers?.find((u) => u.id == entry.by);
        return (
          <div className="rfp-activity-pill">
            <div className="rfp-activity-icon">
              {user?.avatar
                ? <img src={`${configure.apiUrl}${user.avatar}`} alt={user.name}
                    style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                : <i className="fas fa-user" />}
            </div>
            <div>
              <div className="rfp-activity-label">Level 1 <span style={{ fontWeight: 400, textTransform: "none" }}>(Timing)</span></div>
              <div className="rfp-activity-user">{user?.name || `User #${entry.by}`}</div>
              <div className="rfp-activity-time">{new Date(entry.at).toLocaleString()}</div>
            </div>
          </div>
        );
      })()}

      {(() => {
        const entry = stepLogs.step4?.saved?.at(-1);
        if (!entry) return null;
        const user = allUsers?.find((u) => u.id == entry.by);
        return (
          <div className="rfp-activity-pill">
            <div className="rfp-activity-icon">
              {user?.avatar
                ? <img src={`${configure.apiUrl}${user.avatar}`} alt={user.name}
                    style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                : <i className="fas fa-user" />}
            </div>
            <div>
              <div className="rfp-activity-label">Level 2 <span style={{ fontWeight: 400, textTransform: "none" }}>(Costing)</span></div>
              <div className="rfp-activity-user">{user?.name || `User #${entry.by}`}</div>
              <div className="rfp-activity-time">{new Date(entry.at).toLocaleString()}</div>
            </div>
          </div>
        );
      })()}

      {(() => {
        const entry = stepLogs.step5?.approved?.at(-1);
        if (!entry) return null;
        const user = allUsers?.find((u) => u.id == entry.by);
        return (
          <div className="rfp-activity-pill">
            <div className="rfp-activity-icon">
              {user?.avatar
                ? <img src={`${configure.apiUrl}${user.avatar}`} alt={user.name}
                    style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                : <i className="fas fa-user" />}
            </div>
            <div>
              <div className="rfp-activity-label">Final Approval <span style={{ fontWeight: 400, textTransform: "none" }}>(Overview)</span></div>
              <div className="rfp-activity-user">{user?.name || `User #${entry.by}`}</div>
              <div className="rfp-activity-time">{new Date(entry.at).toLocaleString()}</div>
            </div>
          </div>
        );
      })()}

    </div>
  </div>
)} */}


{/* {stepLogs && (
  <div className="rfp-activity-log mt-4">
    <div className="rfp-activity-log-title">Activity</div>
    <div className="d-flex gap-3 flex-wrap">
      {(() => {
        const cards = [
          {
            label: "Created By",
            sublabel: null,
            entry: stepLogs.step2?.saved?.[0],
          },
          {
            label: "Level 1",
            sublabel: "(Timing)",
            entry: stepLogs.step3?.submitted?.at(-1),
          },
          {
            label: "Level 2",
            sublabel: "(Costing)",
            entry: stepLogs.step4?.saved?.at(-1),
          },
          {
            label: "Final Approval",
            sublabel: "(Overview)",
            entry: stepLogs.step5?.approved?.at(-1),
          },
        ];

        // Stop rendering as soon as a card has no entry
        const visibleCards = [];
        for (const card of cards) {
          if (!card.entry) break;
          visibleCards.push(card);
        }

        return visibleCards.map((card, i) => {
          const user = allUsers?.find((u) => u.id == card.entry.by);
          return (
            <div key={i} className="rfp-activity-pill">
              <div className="rfp-activity-icon">
                {user?.avatar
                  ? <img
                      src={`${configure.apiUrl}${user.avatar}`}
                      alt={user.name}
                      style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                    />
                  : <i className="fas fa-user" />}
              </div>
              <div>
                <div className="rfp-activity-label">
                  {card.label}{" "}
                  {card.sublabel && (
                    <span style={{ fontWeight: 400, textTransform: "none" }}>
                      {card.sublabel}
                    </span>
                  )}
                </div>
                <div className="rfp-activity-user">
                  {user?.name || `User #${card.entry.by}`}
                </div>
                <div className="rfp-activity-time">
                  {new Date(card.entry.at).toLocaleString()}
                </div>
              </div>
            </div>
          );
        });
      })()}
    </div>
  </div>
)} */}

{stepLogs && (
  <div className="rfp-activity-log mt-4">
    <div className="rfp-activity-log-title">Activity</div>
    <div className="d-flex gap-3 flex-wrap">
      {(() => {
        // Each card has an entry AND a condition that must be true
        // for it to be considered "active" in the current chain.
        // If the condition is false, the chain breaks there.
        const cards = [
          {
            label: "Created By",
            sublabel: null,
            entry: stepLogs.step2?.saved?.[0],
            // Always show if saved
            active: !!stepLogs.step2?.saved?.[0],
          },
          {
            label: "Level 1",
            sublabel: "(Timing)",
            entry: stepLogs.step3?.submitted?.at(-1),
            // Only active if step3 is currently submitted or approved
            // i.e. step3 still has its approval — not been reset
            active:
              !!stepLogs.step3?.submitted?.at(-1) &&
              !!stepLogs.step3?.approved?.at(-1),
          },
          {
            label: "Level 2",
            sublabel: "(Costing)",
            entry: stepLogs.step4?.saved?.at(-1),
            // Only active if step4 saved AND step3 approved (chain intact)
            active:
              !!stepLogs.step4?.saved?.at(-1) &&
              !!stepLogs.step3?.approved?.at(-1),
          },
          {
            label: "Final Approval",
            sublabel: "(Overview)",
            entry: stepLogs.step5?.approved?.at(-1),
            // Only active if step5 approved AND step4 saved AND step3 approved
            active:
              !!stepLogs.step5?.approved?.at(-1) &&
              !!stepLogs.step4?.saved?.at(-1) &&
              !!stepLogs.step3?.approved?.at(-1),
          },
        ];

        // Stop at first card where active is false
        const visibleCards = [];
        for (const card of cards) {
          if (!card.active) break;
          visibleCards.push(card);
        }

        return visibleCards.map((card, i) => {
          const user = allUsers?.find((u) => u.id == card.entry.by);
          return (
            <div key={i} className="rfp-activity-pill">
              <div className="rfp-activity-icon">
                {user?.avatar
                  ? <img
                      src={`${configure.imgUrl}${user.avatar}`}
                      alt={user.name}
                      style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                    />
                  : <i className="fas fa-user" />}
              </div>
              <div>
                <div className="rfp-activity-label">
                  {card.label}{" "}
                  {card.sublabel && (
                    <span style={{ fontWeight: 400, textTransform: "none" }}>
                      {card.sublabel}
                    </span>
                  )}
                </div>
                <div className="rfp-activity-user">
                  {user?.name || `User #${card.entry.by}`}
                </div>
                <div className="rfp-activity-time">
                  {new Date(card.entry.at).toLocaleString()}
                </div>
              </div>
            </div>
          );
        });
      })()}
    </div>
  </div>
)}


      {/* ── Bottom nav ── */}
      <NavButtons />

    </div>
  )
}

export default StepFeatures